import React, { useEffect } from "react";

import { TFunction, useCalculateCoverCropMixes, useGetCropMixComposerCrops, useGetCropMixComposerScoreFactors, useTranslation } from "utils";

import { Alert, Flex, Icon, PageSpin } from "components";

import { useCropMixComposerContext } from "pages/app/crop-mix-composer/CropMixComposerContextProvider";
import { CMC_ROUTES } from "pages/app/crop-mix-composer/CropMixComposerOverlay";
import { CropsOverviewTable } from "pages/app/crop-mix-composer/components/compositionStep/CropsOverviewTable";
import { ScoreSidePanel } from "pages/app/crop-mix-composer/components/compositionStep/ScoreSidePanel";
import { calculateAggregateMatchScore, calculateMixDistributionShares, generateGoalScores } from "pages/app/crop-mix-composer/utils/cropMixComposer";

export const AutoMixFailureMessage = ({ t, onDismiss }: { t: TFunction; onDismiss: () => void }) => (
  <Alert
    type="info"
    description={
      <Flex align="center" gap="0.5rem">
        <Icon iconName="tabler/alert-circle" />
        {t("page.composition.autoMixGeneration.failureMessage")}
      </Flex>
    }
    closable
    onClose={onDismiss}
    className="smart-mix-failure-message"
  />
);

export const MixComposition = () => {
  const { t } = useTranslation("cropMixComposer");

  const {
    activeCropMixId,
    selectedCrops,
    selectedGoals,
    setAggregateGoalScores,
    setAggregateMatchScore,
    setWeightShareDistribution,
    isPageLoading,
    showAutoMixFailureMessage,
    setShowAutoMixFailureMessage,
  } = useCropMixComposerContext();

  const { data: cropMixComposerResponse } = useCalculateCoverCropMixes({ cropMixId: activeCropMixId });
  const { data: scoreFactors } = useGetCropMixComposerScoreFactors();
  const { data: cropsCMC } = useGetCropMixComposerCrops(activeCropMixId);

  useEffect(() => {
    if (!scoreFactors || !cropMixComposerResponse) return;

    const weightShareDistribution = calculateMixDistributionShares(selectedCrops, cropsCMC);
    setWeightShareDistribution(weightShareDistribution);
    const aggregateGoalScores = generateGoalScores(selectedGoals, weightShareDistribution, scoreFactors, selectedCrops);
    setAggregateGoalScores(aggregateGoalScores);
    const aggregateMatchScore = calculateAggregateMatchScore(selectedCrops, weightShareDistribution);
    setAggregateMatchScore(aggregateMatchScore);
  }, [
    cropMixComposerResponse,
    cropsCMC,
    scoreFactors,
    selectedCrops,
    selectedGoals,
    setAggregateGoalScores,
    setAggregateMatchScore,
    setWeightShareDistribution,
  ]);

  if (isPageLoading) return <PageSpin />;

  return (
    <Flex className="crop-mix-composer-overlay-step-overview" data-testid="crop-mix-composer-overlay-step-overview">
      <Flex className="table-section" vertical>
        {showAutoMixFailureMessage && <AutoMixFailureMessage t={t} onDismiss={() => setShowAutoMixFailureMessage(false)} />}
        <Flex className="table-section-header" vertical>
          <p className="header-title paragraph-m-bold">{t(`page.${CMC_ROUTES.COMPOSITION}.header`)}</p>
          <p className="header-sub-title paragraph-m">{t(`page.${CMC_ROUTES.COMPOSITION}.subHeader`)}</p>
        </Flex>
        {/* TODO: Add button back after lunch when filters and search are implemented */}
        {/* <Flex justify="space-between">
          <Flex gap="0.5rem">
            <Button className="filter-btn" size="small">
              {t(`page.${CMC_ROUTES.COMPOSITION}.filters.all`)}
            </Button>
            <Button className="filter-btn" size="small">
              {t(`page.${CMC_ROUTES.COMPOSITION}.filters.selected`)}
            </Button>
            {selectedGoals?.map((goal, idx) => (
              <Button key={idx} className="filter-btn" size="small">
                {t(`goals.${goal}`)}
              </Button>
            ))}
          </Flex>
          <Flex gap="0.5rem">
            <Button className="filter-btn filter-icon" size="small">
              <Icon iconName="tabler/search" />
            </Button>
            <Button className="filter-btn" size="small">
              <Flex align="center">
                <Icon iconName="tabler/adjustments-horizontal" />
                {t(`page.${CMC_ROUTES.COMPOSITION}.filters.filters`)}
              </Flex>
            </Button>
          </Flex>
        </Flex> */}
        <CropsOverviewTable cropMixComposerResponse={cropMixComposerResponse} />
      </Flex>
      <ScoreSidePanel />
    </Flex>
  );
};
